import { createAction, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import isEqual from 'lodash/isEqual';
import { getThumbnailNew } from 'services/format/thumbnails';
import GaEventManager from 'services/ga/gaEventManager';
import { Actions, Labels } from 'services/ga/interfaces';
import { auctionsBidsActions } from 'state/auctionsBids.slice/auctionsBids.slice';
import { auctionsWatchActions } from 'state/auctionsWatch.slice/auctionsWatch.slice';
import {
  getAttachmentHandler,
  getFilterTextByValues,
  getPersonalizedFiltersInitialState,
  getQuickFiltersInitialState,
  getSortedCertificates,
} from 'state/helpers';
import { locationChange } from 'state/router.actions';
import { countersActions } from './../counters.slice/counters.slice';
import { typeToFilters, typeToPersonalizedFilters, typeToQuickFilters } from './constants';
import { filterAuctions } from './filters';
import {
  flattenAdo,
  generateFilters,
  getColumnTexts,
  getInitialState,
  getSelectNextValue,
} from './helpers';
import { buyerActions } from 'state/buyer.slice';
import { camelCase } from 'lodash';
import { getLocalStorageSort, setLocalStorageSort } from 'helpers/localStorageHelpers';

const fetchAuctions = createAsyncThunk(
  'auctions/fetchAuctions',
  async ({ type }: { type: AuctionsTypes.AuctionTypes }, { getState, dispatch, signal }) => {
    const state = getState() as RootState;
    const { filters, filtersData } = state.auctions;
    const { pending } = state.auctionsWatch;
    const beFilters = generateFilters(type, filters[type], filtersData);

    if (state.auctions.useSplitApis) {
      dispatch(auctionsBidsActions.fetchAuctionsBids());
    }

    if (pending && pending.show) {
      dispatch(
        auctionsWatchActions.setAuctionAsWatching({
          auction: { auction_id: pending?.auctionId as number, watch: pending?.watch as boolean },
        }),
      );
      dispatch(auctionsWatchActions.clearPending());
    }

    const { data }: AxiosResponse<AuctionsTypes.DataType> = await axios.post(
      `/api/buyers/auctions/by/item_type/${type}`,
      {
        type,
        filters: beFilters,
      },
      { signal },
    );

    const auctionsWithCertificates = data.auctions.map((auction) => {
      const hasDocuments = Object.keys(auction.certificates.documents).length > 0;
      const hasCertificates = Object.keys(auction.certificates.provider_and_number).length > 0;
      const certificates = hasCertificates
        ? Object.values(auction.certificates.provider_and_number)
        : [];
      const giaCertificate = certificates.find((cert) => cert.provider === 'GIA');

      if (!hasDocuments && hasCertificates && giaCertificate) {
        return {
          ...auction,
          certificates: {
            ...auction.certificates,
            documents: {
              [-1]: {
                type: 'Old GIA',
                url: `https://www.gia.edu/report-check?reportno=${giaCertificate.number}`,
              },
            },
          },
        };
      }

      return auction;
    });

    const { isFiltered, count, auctions } = filterAuctions(
      type,
      getState() as RootState,
      auctionsWithCertificates,
    );
    const totalsKey = type === 'watches' ? 'luxury_watches' : type;

    if (!isFiltered) {
      dispatch(countersActions.updateCount({ type, count }));
    }
    dispatch(countersActions.updateFilteredCount({ type, count }));

    return {
      data: {
        ...data,
        auctions,
        totals_by_type: {
          [totalsKey]: count,
        },
      },
      rawData: { ...data, auctions: auctionsWithCertificates },
      isFiltered,
      type,
    };
  },
);

const filterAuctionsForType = createAsyncThunk(
  'auctions/filterAuctionsForType',
  async ({ type }: { type?: AuctionsTypes.AuctionTypes }, { getState, dispatch }) => {
    const state = getState() as RootState;
    if (!type || !state.auctions.rawData.auctions) {
      return;
    }

    const { isFiltered, count, auctions } = filterAuctions(
      type,
      getState() as RootState,
      state.auctions.rawData.auctions,
    );
    const totalsKey = type === 'watches' ? 'luxury_watches' : type;

    if (!isFiltered) {
      dispatch(countersActions.updateCount({ type, count }));
    }
    dispatch(countersActions.updateFilteredCount({ type, count }));

    // if we want to add delay for client side filters we need this
    // as well as filterAuctionsForType.pending
    // await wait(500);

    return {
      data: {
        ...state.auctions.rawData,
        auctions,
        totals_by_type: {
          [totalsKey]: count,
        },
      },
      isFiltered,
      type,
    };
  },
);

const applySearch = createAsyncThunk(
  'auctions/applySearch',
  async ({ type }: { type?: AuctionsTypes.AuctionTypes }, { getState, dispatch }) => {
    if (!type) {
      return;
    }

    const state = getState() as RootState;
    const search = state.auctions.search[type];

    const category = Labels[type];

    GaEventManager.sendGAActionData({
      label: `Auction No. / Certificate ID filter`,
      action: Actions.FilterClicked,
      category: category,
      value: search,
    });

    GaEventManager.GA4InputChange('text', 'search', {
      location4: 'filtersPanel',
      actionedObject: 'search',
      actionedValue: search,
    });

    if (!state.auctions.isLoading) {
      dispatch(filterAuctionsForType({ type }));
    }
  },
);

const fetchAuctionAttachments = createAsyncThunk(
  'auctions/fetchAuctionAttachments',
  async (payload: { auction: Partial<AuctionsTypes.AuctionType> }, { getState, signal }) => {
    const { auction } = payload;
    const state = getState() as RootState;
    let rawAttachments = { ...(state.auctions?.rawAttachments || {}) };

    if (!auction?.auction_number) {
      return { attachments: null, rawAttachments };
    }

    const auctionNumber = Number(auction.auction_number);
    let attachments: AuctionsTypes.CachedAttachment = {
      certificates: [],
      images: [],
      video360: [],
      auctionNumber,
    };

    const sessionKey = 'attachments';
    const sessionData = sessionStorage.getItem(sessionKey);

    const cachedData = sessionData ? JSON.parse(sessionData) : {};

    const handlerOnGet = getAttachmentHandler(sessionKey, auctionNumber);

    const cachedAttachments = await new Proxy(cachedData, handlerOnGet);

    if (rawAttachments?.[auctionNumber]) {
      return {
        attachments: rawAttachments?.[auctionNumber],
        rawAttachments,
      };
    }

    if (auctionNumber in cachedData) {
      attachments = cachedAttachments[auctionNumber];
    } else {
      const videoUrl = process.env.REACT_APP_AWS_VIDEO_URL;

      const { data }: { data: { images: string[] } } = await axios.get(
        `/api/buyers/${auction.auction_number}/auction_images`,
        {
          signal,
        },
      );

      attachments.images = data?.images
        ? data.images
            .filter((image) => image.trim() !== '')
            .map((image) => ({
              title: auction.description,
              url: image,
              thumbnail: image,
            }))
        : [];

      attachments.video360 = auction.video_360
        ? [
            {
              title: auction.description,
              url: videoUrl?.replace('{auctionId}', auction.auction_number) ?? '',
              thumbnail: getThumbnailNew('360'),
            },
          ]
        : [];

      if (auction?.certificates?.documents) {
        const sortedCertificates = getSortedCertificates(auction?.certificates?.documents);

        attachments.certificates = sortedCertificates.map((document) => {
          const thumbnail =
            document.type && document.type.toLowerCase().search('gia') > -1 ? 'gia' : undefined;

          return {
            title: 'Certificates',
            url: document.url,
            thumbnail: getThumbnailNew(thumbnail),
          };
        });
      }

      cachedAttachments[auctionNumber] = attachments;
    }

    rawAttachments[auctionNumber] = attachments;

    return {
      attachments,
      rawAttachments,
    };
  },
);

const fetchAndShowAttachments = createAsyncThunk(
  'auctions/fetchAndShowAttachments',
  async (
    payload: {
      auction: AuctionsTypes.AuctionType;
      type: AuctionsTypes.AuctionTypes;
      attachmentType: AuctionsTypes.AttachmentTypes;
      eventOrigin?: string;
    },
    { getState, dispatch },
  ) => {
    const { type, attachmentType, eventOrigin } = payload;
    const state = getState() as RootState;
    if (state.auctions.attachment.isShow) {
      dispatch(auctionsActions.closeAttachmentDialog());
    }

    if (state.auctions.attachment.auctionPreviewImageId) {
      dispatch(auctionsActions.fetchAndShowPreview({}));
    }

    await dispatch(fetchAuctionAttachments(payload));
    dispatch(auctionsActions.showAttachmentDialog({ type, attachmentType, eventOrigin }));

    return;
  },
);

const fetchAndShowPreview = createAsyncThunk(
  'auctions/fetchAndShowPreview',
  async (
    payload: { auction?: Partial<AuctionsTypes.AuctionType>; showPreview?: boolean },
    { dispatch },
  ) => {
    const { auction } = payload;

    if (!auction || !payload) {
      return {
        auctionPreviewImageId: undefined,
      };
    }

    await dispatch(fetchAuctionAttachments({ auction }));

    return {
      auctionPreviewImageId: auction.auction_id,
    };
  },
);

export const initialState: AuctionsTypes.AuctionsStateType = {
  data: {},
  rawData: {},
  isFiltered: false,
  isLoading: true,
  filteredAuctions: {},
  filters: {
    diamonds: [],
    jewelry: [],
    watches: [],
  },
  quickFilters: {
    diamonds: [],
    jewelry: [],
    watches: [],
  },
  personalizedFilters: {
    diamonds: [],
    jewelry: [],
    watches: [],
  },
  search: {
    diamonds: '',
    jewelry: '',
    watches: '',
  },
  filtersData: {},
  useSplitApis: undefined,
  sort: getLocalStorageSort(),
  attachment: {
    type: undefined,
    isShow: false,
    auctionPreviewImageId: undefined,
    attachments: null,
    selectedAttachment: undefined,
  },
  rawAttachments: null,
  suggested: [],
  viewed: [],
  trending: [],
  promoted: [],
  experiments: {},
};

export const auctionsSlice = createSlice({
  name: 'auctions',
  initialState,
  reducers: {
    setFiltersInitialData: (state, action) => {
      const filtersData = flattenAdo(action.payload) || {};
      const filters = getInitialState<typeof typeToFilters>(typeToFilters, filtersData);
      const quickFilters = getQuickFiltersInitialState(typeToQuickFilters);
      const personalizedFilters = getPersonalizedFiltersInitialState(typeToPersonalizedFilters);
      const useSplitApis = action.payload.use_split_apis;

      return {
        ...state,
        filters,
        quickFilters,
        personalizedFilters,
        filtersData,
        useSplitApis,
      };
    },
    onFilterChange: (
      state,
      action: PayloadAction<{
        type: AuctionsTypes.AuctionTypes;
        index: number;
        value: any;
        filterType: 'filters' | 'quickFilters' | 'personalizedFilters';
      }>,
    ) => {
      const { type, index, value, filterType } = action.payload;
      const category = Labels[type];

      const changedFilters = state[filterType];
      const nextStateForType = [...changedFilters[type]];
      const changedField = { ...nextStateForType[index] };
      let newValue = value;

      if (changedField.type === 'toggle' && changedField.name === 'fancy') {
        // it works if toggle is after changed field
        if (nextStateForType[index - 1]) {
          nextStateForType[index - 1] = {
            ...nextStateForType[index - 1],
            isDisabled: newValue,
          };
        }
      }
      if (changedField.type === 'select') {
        newValue = getSelectNextValue(changedField.value as number[], newValue);
      }

      changedField.value = newValue;
      nextStateForType[index] = {
        ...nextStateForType[index],
        value: newValue,
        isChanged: !isEqual(newValue, nextStateForType[index].initialValue),
      };

      const filterValue = getFilterTextByValues(changedField);
      if (filterValue && filterValue.length > 0) {
        GaEventManager.sendGAActionData({
          label: `${changedField.name} filter`,
          action: Actions.FilterClicked,
          category: category,
          value: filterValue,
        });

        GaEventManager.GA4InputChange(changedField.type, changedField.name, {
          location4: 'filtersPanel',
          actionedObject: changedField.name,
          actionedValue: filterValue,
        });
      }

      return {
        ...state,
        [filterType]: {
          ...state[filterType],
          [type]: nextStateForType,
        },
      };
    },
    onFilterReset: (
      state,
      action: PayloadAction<{ type: AuctionsTypes.AuctionTypes; index: number }>,
    ) => {
      const { type, index } = action.payload;
      const category = Labels[type];

      state.filters[type][index].value = state.filters[type][index].initialValue;
      state.filters[type][index].isChanged = false;

      const changedField = state.filters[type][index];

      const filterValue = getFilterTextByValues(changedField);
      if (filterValue && filterValue.length > 0) {
        GaEventManager.sendGAActionData({
          label: `${changedField.name} filter`,
          action: Actions.FilterClicked,
          category: category,
          value: filterValue,
        });

        GaEventManager.GA4SendActionClear({
          location4: 'filtersPanel',
          actionedObject: changedField.name,
        });
      }
    },
    onFiltersReset: (state, action: PayloadAction<{ type: AuctionsTypes.AuctionTypes }>) => {
      const { type } = action.payload;
      const category = Labels[type];

      GaEventManager.sendGAActionData({
        label: category,
        action: Actions.ClearFiltersClicked,
        category: 'Active auctions',
      });

      GaEventManager.GA4SendActionClear({
        location4: 'filtersPanel',
        actionedObject: 'allFilters',
      });

      state.filters[type] = getInitialState(typeToFilters, state.filtersData)[type];
      state.quickFilters[type] = getQuickFiltersInitialState(typeToQuickFilters)[type];
      state.personalizedFilters[type] =
        getPersonalizedFiltersInitialState(typeToPersonalizedFilters)[type];
      state.search[type] = '';
      state.isFiltered = false;
      state.filteredAuctions[type] = false;
    },
    setSearchForType: (
      state,
      action: PayloadAction<{ type?: AuctionsTypes.AuctionTypes; search: string }>,
    ) => {
      const { type, search } = action.payload;

      if (!type) {
        return state;
      }

      return {
        ...state,
        search: {
          ...state.search,
          [type]: search,
        },
        auctionPreview: {
          auction: null,
          isOpen: false,
          isLoading: false,
        },
      };
    },
    clearSearchForType: (state, action: PayloadAction<{ type?: AuctionsTypes.AuctionTypes }>) => {
      const { type } = action.payload;

      if (!type) {
        return state;
      }

      return {
        ...state,
        search: {
          ...state.search,
          [type]: '',
        },
        auctionPreview: {
          auction: null,
          isOpen: false,
          isLoading: false,
        },
      };
    },
    setSortForType: (
      state,
      action: PayloadAction<{ type?: AuctionsTypes.AuctionTypes; col: string }>,
    ) => {
      const { type, col } = action.payload;

      if (!type) {
        return state;
      }

      const category = Labels[type];

      const sort = {
        col,
        asc: col !== state.sort[type].col ? true : !state.sort[type].asc,
      };

      const columnTitle = getColumnTexts(sort.col, type);

      setLocalStorageSort(type, sort);

      GaEventManager.sendGAActionData({
        label: category,
        action: Actions.SortClicked,
        category: 'Active auctions',
        value: `Sorted by ${columnTitle} ${sort.asc ? 'ascending' : 'descending'}`,
      });

      GaEventManager.GA4SendActionSort({
        location4: 'auctionsTable',
        actionedObject: camelCase(columnTitle),
        actionedValue: sort.asc ? 'ascending' : 'descending',
      });

      return {
        ...state,
        sort: {
          ...state.sort,
          [type]: sort,
        },
      };
    },
    setSortDirectionForType: (
      state,
      action: PayloadAction<{ type?: AuctionsTypes.AuctionTypes; direction: 'asc' | 'desc' }>,
    ) => {
      const { type, direction } = action.payload;

      if (!type) {
        return state;
      }

      const category = Labels[type];

      const sort = {
        ...state.sort[type],
        asc: direction === 'asc',
      };

      const columnTitle = getColumnTexts(sort.col, type);

      setLocalStorageSort(type, sort);

      GaEventManager.sendGAActionData({
        label: category,
        action: Actions.SortClicked,
        category: 'Active auctions',
        value: `Sorted by ${columnTitle} ${sort.asc ? 'ascending' : 'descending'}`,
      });

      GaEventManager.GA4SendActionSort({
        location4: 'auctionsTable',
        actionedObject: camelCase(columnTitle),
        actionedValue: sort.asc ? 'ascending' : 'descending',
      });

      return {
        ...state,
        sort: {
          ...state.sort,
          [type]: sort,
        },
      };
    },
    showAttachmentDialog: (
      state,
      action: PayloadAction<{
        type: AuctionsTypes.AuctionTypes;
        attachmentType: AuctionsTypes.AttachmentTypes;
        selectedAttachment?: number;
        eventOrigin?: string;
      }>,
    ) => {
      const { attachmentType, type, selectedAttachment, eventOrigin } = action.payload;
      const {
        attachment: { attachments },
      } = state;

      if (!attachments || !attachmentType || !attachments[attachmentType]) {
        return {
          ...state,
          attachment: {
            ...state.attachment,
            isShow: false,
            type: attachmentType,
            selectedAttachment,
          },
        };
      }

      const category = Labels[type];
      let gaAction;

      if (attachmentType === 'certificates') {
        gaAction = Actions.CertificateClicked;
      } else if (attachmentType === 'images') {
        gaAction = Actions.MediaImagesClicked;
      } else {
        gaAction = Actions.Media360Clicked;
      }

      GaEventManager.sendGAActionData({
        label: category,
        action: gaAction,
        category: 'Active auctions',
        // TODO: get this auction number
        // auctionId: auction?.auction_number,
      });

      GaEventManager.GA4SendActionOpen({
        location4: eventOrigin,
        actionedObject: attachmentType,
        fieldName: 'auctionId',
        fieldValue: String(state.attachment.attachments?.auctionNumber),
      });

      return {
        ...state,
        attachment: {
          ...state.attachment,
          isShow: attachments[attachmentType].length > 0,
          type: attachmentType,
          selectedAttachment,
        },
      };
    },
    closeAttachmentDialog: (state) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          type: undefined,
          isShow: false,
          selectedAttachment: undefined,
        },
      };
    },
    hideAttachmentImagePreview: (state) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          auctionPreviewImageId: undefined,
        },
      };
    },
    onOpenAuction: (state, action: PayloadAction<{ auction_id: number }>) => {
      return {
        ...state,
        viewed: Array.from(new Set([...state.viewed, action.payload.auction_id])),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuctions.pending, (state, action) => {
      return {
        ...state,
        isLoading: true,
        data: {},
        rawData: {},
        isFiltered: state?.filteredAuctions[action.meta.arg.type] || state.isFiltered,
      };
    });
    builder.addCase(fetchAuctions.fulfilled, (state, action) => {
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        rawData: action.payload.rawData,
        isFiltered: action.payload.isFiltered,
        type: action.payload.type,
        filteredAuctions: {
          ...state.filteredAuctions,
          [action.payload.type]: action.payload.isFiltered,
        },
      };
    });
    // builder.addCase(filterAuctionsForType.pending, (state) => {
    //   return {
    //     ...state,
    //     isLoading: true,
    //   };
    // });
    builder.addCase(filterAuctionsForType.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      return {
        ...state,
        isLoading: false,
        data: action.payload.data,
        isFiltered: action.payload.isFiltered,
        filteredAuctions: {
          ...state.filteredAuctions,
          [action.payload.type]: action.payload.isFiltered,
        },
      };
    });
    builder.addCase(fetchAuctionAttachments.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      return {
        ...state,
        attachment: {
          ...state.attachment,
          attachments: action.payload.attachments,
        },
        rawAttachments: action.payload.rawAttachments,
      };
    });
    builder.addCase(fetchAuctionAttachments.rejected, (state) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          attachments: null,
        },
      };
    });
    builder.addCase(fetchAndShowPreview.fulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }

      return {
        ...state,
        attachment: {
          ...state.attachment,
          auctionPreviewImageId: action.payload.auctionPreviewImageId,
        },
      };
    });
    // actions triggered by other reducers
    builder.addCase(locationChange, (state, action) => {
      if (
        action.payload.location.pathname.includes('/auctions') &&
        action.payload.action === 'PUSH'
      ) {
        return {
          ...state,
          isLoading: true,
          data: {},
          rawData: {},
        };
      }
      return state;
    });
    builder.addCase(createAction('auctionsPreview/fetchAuctionDetails/pending'), (state) => {
      return {
        ...state,
        attachment: {
          ...state.attachment,
          type: undefined,
          isShow: false,
          selectedAttachment: undefined,
        },
      };
    });
    builder.addCase(buyerActions.fetchPersonalizedAuctions.fulfilled, (state, action) => {
      const trendingExperimentActive =
        action.payload.experiments.hasOwnProperty('trendingAuctions');
      const trending = !trendingExperimentActive
        ? action.payload.trendingAuctions
        : action.payload.trendingAuctions.filter((auctionId) =>
            action.payload.experiments.trendingAuctions.includes(auctionId),
          );

      const suggestedExperimentActive =
        action.payload.experiments.hasOwnProperty('suggestedAuctions');
      const suggested = !suggestedExperimentActive
        ? action.payload.suggestedAuctions
        : action.payload.suggestedAuctions.filter((auctionId) =>
            action.payload.experiments.suggestedAuctions.includes(auctionId),
          );

      return {
        ...state,
        trending,
        suggested,
        viewed: action.payload.viewedAuctions,
        promoted: action.payload.promotedAuctions,
      };
    });
    builder.addCase(buyerActions.updatePersonalizedAuctions.fulfilled, (state, action) => {
      const trendingExperimentActive = state.experiments.hasOwnProperty('trendingAuctions');
      const trendingData = action.payload.trendingAuctions || state.trending;
      const trending = !trendingExperimentActive
        ? trendingData
        : trendingData.filter((auctionId) =>
            state.experiments.trendingAuctions.includes(auctionId),
          );

      const suggestedExperimentActive = state.experiments.hasOwnProperty('suggestedAuctions');
      const suggestedData = action.payload.suggestedAuctions || state.suggested;
      const suggested = !suggestedExperimentActive
        ? suggestedData
        : suggestedData.filter((auctionId) =>
            state.experiments.suggestedAuctions.includes(auctionId),
          );

      return {
        ...state,
        trending,
        suggested,
        viewed: action.payload.viewedAuctions || state.viewed,
      };
    });
  },
});

export const auctionsActions = {
  ...auctionsSlice.actions,
  fetchAuctions,
  filterAuctionsForType,
  applySearch,
  fetchAuctionAttachments,
  fetchAndShowAttachments,
  fetchAndShowPreview,
};

export default auctionsSlice.reducer;
